import {
	type CSSProperties,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { cx } from "@/utils/cx";

const GAP = 8;
const VIEWPORT_PADDING = 8;

type TruncatedTextProps = {
	text?: string | null;
	maxWidth?: CSSProperties["maxWidth"];
	className?: string;
	tooltipMaxWidth?: number;
	tabIndex?: number;
};

/** Shows the complete text only when its rendered label overflows. */
export function TruncatedText({
	text,
	maxWidth,
	className,
	tooltipMaxWidth = 384,
	tabIndex = 0,
}: TruncatedTextProps) {
	const id = useId();
	const textRef = useRef<HTMLSpanElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const [anchor, setAnchor] = useState<DOMRect | null>(null);
	const [position, setPosition] = useState<{
		top: number;
		left: number;
		above: boolean;
	} | null>(null);
	useEffect(() => setAnchor(null), [text]);

	const close = () => setAnchor(null);
	const open = () => {
		const element = textRef.current;
		if (!text || !element || element.scrollWidth <= element.clientWidth + 1)
			return;
		setPosition(null);
		setAnchor(element.getBoundingClientRect());
	};

	useLayoutEffect(() => {
		const tooltip = tooltipRef.current;
		if (!anchor || !tooltip) return;
		const above = anchor.top - tooltip.offsetHeight - GAP >= VIEWPORT_PADDING;
		const halfWidth = tooltip.offsetWidth / 2;
		setPosition({
			top: above ? anchor.top - GAP : anchor.bottom + GAP,
			left: Math.max(
				VIEWPORT_PADDING + halfWidth,
				Math.min(
					anchor.left + anchor.width / 2,
					window.innerWidth - VIEWPORT_PADDING - halfWidth
				)
			),
			above,
		});
	}, [anchor, text]);

	useEffect(() => {
		if (!anchor) return;
		const hide = () => setAnchor(null);
		window.addEventListener("scroll", hide, true);
		window.addEventListener("resize", hide);
		return () => {
			window.removeEventListener("scroll", hide, true);
			window.removeEventListener("resize", hide);
		};
	}, [anchor]);

	if (!text) return <span>-</span>;

	return (
		<>
			{/* biome-ignore lint/a11y/noNoninteractiveTabindex: keyboard access to truncated text */}
			{/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: text tooltip supports hover and keyboard focus */}
			<span
				aria-describedby={anchor ? id : undefined}
				className={cx(
					"block min-w-0 cursor-default truncate outline-hidden",
					className
				)}
				onBlur={close}
				onFocus={open}
				onKeyDown={(event) => {
					if (event.key === "Escape") close();
				}}
				onMouseEnter={open}
				onMouseLeave={close}
				ref={textRef}
				style={{ maxWidth }}
				tabIndex={tabIndex}
			>
				{text}
			</span>
			{anchor
				? createPortal(
						<div
							className={cx(
								"pointer-events-none fixed -translate-x-1/2 rounded-lg bg-primary-solid px-3 py-2 font-semibold text-white text-xs shadow-lg",
								position?.above && "-translate-y-full"
							)}
							data-react-aria-top-layer="true"
							id={id}
							ref={tooltipRef}
							role="tooltip"
							style={{
								top: position?.top ?? 0,
								left: position?.left ?? 0,
								visibility: position ? "visible" : "hidden",
								zIndex: 100_000,
								width: "max-content",
								maxWidth: `min(${tooltipMaxWidth}px, calc(100vw - 16px))`,
								wordBreak: "break-word",
								whiteSpace: "normal",
							}}
						>
							{text}
						</div>,
						document.body
					)
				: null}
		</>
	);
}
